// ---------------------------------------------------------------------------
// Stale chunks after a deploy (AL-2004)
// ---------------------------------------------------------------------------
// A tab opened before a deploy still holds the old index, so the first visit
// to a lazy route asks for a chunk hash the server no longer has. The fix is
// a reload, which fetches the new index — but only one per build: if the
// chunk is still missing after it, reloading again would loop forever.
//
// The guard remembers which build it reloaded from (the hashed entry script),
// not when: a time window loops on a connection slow enough to outlast it,
// and a tab that lives through several deploys must still recover from each.
// ---------------------------------------------------------------------------

import { reloadPage } from './reloadPage';

export const CHUNK_RELOAD_KEY = 'al:chunk-reload-build';
/** How long a requested reload may take before we stop assuming it happened. */
export const RELOAD_PENDING_MS = 5_000;

// Chrome, Firefox and Safari word the failed import differently; the last two
// patterns are Vite's own (CSS preload) and webpack-style chunk errors.
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /loading (css )?chunk [\w-]+ failed/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(error.message));
}

/**
 * The build this page runs: Vite's entry script carries the build hash
 * (`/assets/index-<hash>.js`). In dev it is `/src/main.tsx`, so dev reloads
 * at most once per tab — which is fine, dev has no stale chunks.
 */
export function currentBuildId(doc: Document = document): string {
  const entry = doc.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return entry?.src || 'unknown';
}

interface ReloadDeps {
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'>;
  readonly buildId?: string;
  readonly online?: boolean;
  readonly now?: () => number;
  readonly reload?: () => void;
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

// When the reload was requested. Module state on purpose: the reload resets
// it along with the page.
let reloadRequestedAt: number | undefined;

/**
 * Reloads the page unless this build already reloaded for a stale chunk.
 * Returns whether it reloaded, so the caller can let the error surface.
 *
 * It doesn't reload:
 * - offline — the import failed for lack of network, not because of a deploy,
 *   and a reload would swap the app for the browser's offline page;
 * - without storage (private mode, blocked site data) — there is no way to
 *   tell a first attempt from a loop.
 */
export function reloadForStaleChunk({
  storage = defaultStorage(),
  buildId = currentBuildId(),
  online = typeof navigator === 'undefined' || navigator.onLine !== false,
  now = Date.now,
  reload = reloadPage,
}: ReloadDeps = {}): boolean {
  if (!online || !storage) return false;

  try {
    if (storage.getItem(CHUNK_RELOAD_KEY) === buildId) return false;
    storage.setItem(CHUNK_RELOAD_KEY, buildId);
  } catch {
    return false;
  }

  reloadRequestedAt = now();
  reload();
  return true;
}

/**
 * True between asking for the reload and the page going away. Cancelling
 * `vite:preloadError` makes the import resolve to undefined, so the route's
 * `.then(m => m.Page)` throws a TypeError in that window — the boundaries
 * render nothing and Sentry drops it, instead of a flash and a false report.
 *
 * It expires: if the page doesn't go away (a cancelled unload), the user must
 * get the fallback back and Sentry its events, not a blank page for good.
 */
export function isReloadingForStaleChunk(now: () => number = Date.now): boolean {
  return reloadRequestedAt !== undefined && now() - reloadRequestedAt < RELOAD_PENDING_MS;
}

/** Tests only: the reload is mocked there, so the page never resets the state. */
export function resetStaleChunkReloadForTests(): void {
  reloadRequestedAt = undefined;
}

/**
 * Vite dispatches `vite:preloadError` when a dynamic import or its preloaded
 * CSS fails. Cancelling the event swallows the error, so it is cancelled only
 * when a reload is actually on its way; otherwise the error reaches the route
 * boundary, which shows the "new version" fallback with a Reload button.
 */
export function installChunkReloadHandler(target: Window = window): () => void {
  const onPreloadError = (event: Event) => {
    if (reloadForStaleChunk()) event.preventDefault();
  };

  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
